// Migrate P2 → Guardian 2 — one-time admin tool to clean up the legacy
// p2FirstName / p2LastName / p2Email / p2Phone fields on user docs by:
//   1. Copying any missing p2* values to guardian2* (the new schema)
//   2. Deleting the p2* fields from those docs
// Idempotent: safe to re-run. Can be removed from the codebase once
// the migration is complete and verified.

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Stack,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const DARK = '#1E3A5F';

export default function MigrateP2() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function run() {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const fns = getFunctions();
      const migrate = httpsCallable(fns, 'migrateP2ToGuardian2');
      const r = await migrate();
      setResult(r.data);
    } catch (e) {
      console.error('Migration error:', e);
      setError(e.message || 'Migration failed.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>
          Migrate p2* → guardian2*
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          One-time migration. Cleans up the legacy <code>p2*</code> fields on user docs
          by copying values to <code>guardian2*</code> (where missing) and deleting <code>p2*</code>.
          Safe to re-run.
        </Typography>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: '#444' }}>
              <strong>What this does:</strong>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                <li>For each user with any <code>p2*</code> field set, copies the value
                    to <code>guardian2*</code> only if the corresponding <code>guardian2*</code> field is missing (existing values are not overwritten).</li>
                <li>Email is lowercase-normalized when copied.</li>
                <li>Deletes all four <code>p2*</code> fields from the doc.</li>
                <li>Users without any <code>p2*</code> fields are skipped (most users).</li>
              </ul>
            </Typography>
            <Button
              variant="contained" startIcon={running ? <CircularProgress size={18} sx={{ color: 'white' }}/> : <PlayArrowIcon />}
              onClick={run} disabled={running}
              sx={{ alignSelf: 'flex-start', background: DARK, fontWeight: 700 }}
            >
              {running ? 'Running…' : 'Run Migration'}
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Done.</strong><br />
            Total users: {result.total}<br />
            Values copied to guardian2*: {result.copied}<br />
            p2* fields cleared: {result.cleared}<br />
            Skipped (no p2* fields): {result.skipped}
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <Box sx={{ mt: 1, color: '#C0392B' }}>
                <strong>Batch errors ({result.errors.length}):</strong>
                <ul>{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </Box>
            )}
          </Alert>
        )}
      </Box>
    </Layout>
  );
}
